/**
 * Patient Service for checking existing patient records
 */

// Get the API base URL based on environment
export const getApiBaseUrl = () => {
  // In development, use localhost:3001 for the backend
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:3001';
  }
  // In production, use relative URLs (same server)
  return '';
};

/**
 * Check for existing patient record based on key identifying fields
 * @param {string} practice - Practice name
 * @param {string} name - Full name
 * @param {string} dateOfBirth - Date of birth (YYYY-MM-DD format)
 * @param {string} gender - Gender
 * @param {string} cellPhone - Cell phone number
 * @returns {Promise<Object>} - Response with success status and patient data if found
 */
export const checkExistingPatient = async (practice, name, dateOfBirth, gender, cellPhone) => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    console.log('🌐 API SERVICE: Making request to check existing patient');
    console.log('🎯 API URL:', `${apiBaseUrl}/api/check-existing-patient`);
    console.log('📦 Request payload:', { practice, name, dateOfBirth, gender, cellPhone });
    
    const response = await fetch(`${apiBaseUrl}/api/check-existing-patient`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        practice, 
        name, 
        dateOfBirth, 
        gender, 
        cellPhone 
      }),
    });

    console.log('📊 API SERVICE: Response status:', response.status, response.statusText);
    
    const data = await response.json();
    console.log('📋 API SERVICE: Response data:', data);
    
    if (!response.ok) {
      console.error('❌ API SERVICE: Request failed:', data.error);
      throw new Error(data.error || 'Failed to check existing patient');
    }
    
    if (data.found) {
      console.log('✅ API SERVICE: Patient match found, record ID:', data.record?.id);
    } else {
      console.log('❌ API SERVICE: No patient match found');
    }
    
    return {
      success: true,
      found: data.found,
      record: data.record
    };
  } catch (error) {
    console.error('💥 API SERVICE ERROR: Failed to check existing patient:', error);
    return {
      success: false,
      found: false,
      record: null,
      error: error.message || 'An error occurred while checking for existing patient',
    };
  }
};

// Fetch active practices for dropdown selection
export const getActivePractices = async () => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    const response = await fetch(`${apiBaseUrl}/api/practices`);
    const data = await response.json();
    
    if (data.success) {
      return data.practices;
    } else {
      throw new Error(data.error || 'Failed to fetch practices');
    }
  } catch (error) {
    console.error('Error fetching practices:', error);
    throw error;
  }
};

// Fetch practice-specific concerns for dropdown selection
export const getPracticeConcerns = async (practiceName) => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    const encodedPracticeName = encodeURIComponent(practiceName);
    const response = await fetch(`${apiBaseUrl}/api/practices/${encodedPracticeName}/concerns`);
    const data = await response.json();
    
    if (data.success) {
      return data.concerns;
    } else {
      throw new Error(data.error || 'Failed to fetch practice concerns');
    }
  } catch (error) {
    console.error('Error fetching practice concerns:', error);
    throw error;
  }
};

// Check if a contact form submitter is an established patient of the practice
export const checkContactPatient = async (practice, firstName, lastName, cellPhone, email) => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    const response = await fetch(`${apiBaseUrl}/api/check-contact-patient`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ practice, firstName, lastName, cellPhone, email })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to check patient status');
    return { success: true, found: data.found };
  } catch (error) {
    console.error('Error in checkContactPatient service:', error);
    return { success: false, found: false };
  }
};

// Fetch active providers (physicians) for a practice from its EMR database
export const getProviders = async (practiceCode) => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    const response = await fetch(`${apiBaseUrl}/api/providers/${encodeURIComponent(practiceCode)}`);
    if (!response.ok) throw new Error('Failed to fetch providers');
    return await response.json();
  } catch (error) {
    console.error('Error fetching providers:', error);
    return [];
  }
};

// Fetch surgeons (active physicians) for a practice from its EMR database
export const getSurgeons = async (practiceCode) => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    const response = await fetch(`${apiBaseUrl}/api/surgeons/${encodeURIComponent(practiceCode)}`);
    if (!response.ok) throw new Error('Failed to fetch surgeons');
    const data = await response.json();
    return data.success ? (data.surgeons || []) : [];
  } catch (error) {
    console.error('Error fetching surgeons:', error);
    return [];
  }
};

// Fetch questions for a specific concern
export const getConcernQuestions = async (concernId) => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    const response = await fetch(`${apiBaseUrl}/api/concerns/${concernId}/questions`);
    const data = await response.json();
    
    if (data.success) {
      return data.questions;
    } else {
      throw new Error(data.error || 'Failed to fetch concern questions');
    }
  } catch (error) {
    console.error('Error fetching concern questions:', error);
    throw error;
  }
};

// Fetch EMR referring providers and organizations
export const getReferringProviders = async (practiceIdentifier) => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    const response = await fetch(`${apiBaseUrl}/api/referring-providers/${encodeURIComponent(practiceIdentifier)}`);
    if (!response.ok) throw new Error('Failed to fetch referring providers');
    return await response.json(); // returns { success: true, providers: [...], organizations: [...] }
  } catch (error) {
    console.error('Error fetching referring providers:', error);
    return { success: false, providers: [], organizations: [] };
  }
};
